import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// Types (extracted from Dashboard.tsx and AgentSettings.tsx)
export interface Agent {
  id: string;
  businessName: string;
  businessType: string;
  calendarEnabled: boolean;
  createdAt: string;
}

export interface AgentConfig {
  id: string;
  businessName: string;
  businessType: string;
  businessDescription: string;
  tone: string;
  faqsPolicies: string;
  extractedDocuments: string[];
  locationInfo: {
    businessName?: string;
    phone?: string;
    address?: string;
    businessHours?: {
      start?: string;
      end?: string;
    };
    timezone?: string;
  };
  settings: {
    responseLength: string;
    greetingMessage: string;
    fallbackResponse: string;
    operatingHours: {
      enabled: boolean;
      timezone: string;
      schedule: Record<string, { enabled: boolean; start: string; end: string }>;
    };
    booking: {
      enabled: boolean;
      defaultDuration: number;
      bufferTimeBetweenAppointments?: number;
      requiresApproval?: boolean;
      breaks?: Array<{ start: string; end: string }>;
      services: Array<{ name: string; duration: number; price: string }>;
      confirmationTemplate: string;
      dailyLimit?: number | null;
      serviceSpecificLimits?: Array<{ serviceName: string; dailyLimit: number }>;
    };
    analytics: {
      enabled: boolean;
      trackConversations: boolean;
    };
  };
  calendarEnabled: boolean;
  isActive: boolean;
  socialLinks?: Array<{
    platform: string;
    url: string;
    label?: string;
    isVisible?: boolean;
    order?: number;
  }>;
  logoUrl?: string;
  tagline?: string;
  suggestedQuestions?: string[];
  approvedServices?: Array<{
    name: string;
    duration?: number;
    documentDuration?: number | null;
    isAIExtracted: boolean;
    stockStatus?: 'in_stock' | 'low_stock' | 'out_of_stock';
    approvedAt: string;
  }>;
}


export interface AnalyticsData {
  timeframe: string;
  dateRange: {
    start: string;
    end: string;
  };
  metrics: {
    totalBookings: {
      count: number;
    };
    mostBookedServices: Array<{
      service: string;
      bookings: number;
    }>;
    totalConversations: {
      count: number;
      totalMessages: number;
    };
    averageConversationDuration: {
      averageMinutes: number;
      averageSeconds: number;
      totalMinutes: number;
      conversationCount: number;
    };
    commonQuestions: Array<{
      category: string;
      count: number;
      topQuestions: Array<{
        question: string;
        confidence: number;
      }>;
    }>;
    unknownQuestions: Array<{
      question: string;
      customerName: string;
      customerContact: string;
      timestamp: string;
      forwarded: boolean;
    }>;
    afterHoursConversations: {
      count: number;
      totalMessages: number;
    };
    userTypes: Array<{
      userType: string;
      count: number;
    }>;
    entrySources: Array<{
      source: string;
      count: number;
    }>;
    estimatedStaffTimeSaved: {
      totalMinutes: number;
      totalHours: number;
      conversationCount: number;
      averagePerConversation: number;
    };
    serviceAnalytics: {
      serviceMentions: Array<{
        service: string;
        mentions: number;
        conversationCount: number;
        avgConfidence: number;
        sources: string[];
      }>;
      serviceInquiries: {
        totalInquiries: number;
        conversationCount: number;
      };
    };
    serviceBookingConversion: Array<{
      service: string;
      totalMentions: number;
      conversationCount: number;
      bookingConversations: number;
      conversionRate: number;
    }>;
  };
}

// API helper function
const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

// Query Keys
export const dashboardKeys = {
  all: ['dashboard'] as const,
  agents: () => [...dashboardKeys.all, 'agents'] as const,
  agent: (id: string) => [...dashboardKeys.all, 'agent', id] as const,
  agentSettings: (id: string) => [...dashboardKeys.agent(id), 'settings'] as const,
  analytics: (agentId: string, timeframe: string) => [...dashboardKeys.agent(agentId), 'analytics', timeframe] as const,
};

// Custom Hooks

// Fetch agents list
export const useAgents = () => {
  return useQuery({
    queryKey: dashboardKeys.agents(),
    queryFn: async (): Promise<{
      agents: Agent[];
      agentCount: {
        current: number;
        max: number;
        remaining: number;
        canCreateMore: boolean;
      };
    }> => {
      return apiRequest('/api/agents');
    },
    staleTime: 30 * 1000, // 30 seconds - balance between freshness and performance
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Fetch individual agent settings
export const useAgentSettings = (agentId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: dashboardKeys.agentSettings(agentId),
    queryFn: async (): Promise<{ agent: AgentConfig }> => {
      return apiRequest(`/api/agents/${agentId}/settings`);
    },
    enabled: enabled && !!agentId,
    staleTime: 10 * 60 * 1000, // 10 minutes (agent settings change less frequently)
    gcTime: 15 * 60 * 1000, // 15 minutes
  });
};

// Mutations

// Update agent settings
export const useUpdateAgent = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ agentId, agent }: { agentId: string; agent: AgentConfig }): Promise<void> => {
      await apiRequest(`/api/agents/${agentId}/settings`, {
        method: 'PUT',
        body: JSON.stringify(agent),
      });
    },
    onSuccess: (_, variables) => {
      // Invalidate and refetch the specific agent settings
      queryClient.invalidateQueries({ queryKey: dashboardKeys.agentSettings(variables.agentId) });
      // Also invalidate the agents list in case basic info changed
      queryClient.invalidateQueries({ queryKey: dashboardKeys.agents() });
    },
  });
};

// Delete agent
export const useDeleteAgent = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (agentId: string): Promise<void> => {
      await apiRequest(`/api/agents/${agentId}`, {
        method: 'DELETE',
      });
    },
    onSuccess: async (_, agentId) => {
      // Remove the deleted agent from cache
      queryClient.removeQueries({ queryKey: dashboardKeys.agent(agentId) });
      // Force immediate refetch of agents list to update count
      await queryClient.refetchQueries({ queryKey: dashboardKeys.agents() });
    },
  });
};

// Toggle agent active status
export const useToggleAgent = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ agentId, isActive }: { agentId: string; isActive: boolean }): Promise<void> => {
      await apiRequest(`/api/agents/${agentId}/toggle`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive }),
      });
    },
    onSuccess: (_, variables) => {
      // Invalidate both agent settings and agents list
      queryClient.invalidateQueries({ queryKey: dashboardKeys.agentSettings(variables.agentId) });
      queryClient.invalidateQueries({ queryKey: dashboardKeys.agents() });
    },
  });
};

// Create agent
export const useCreateAgent = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (agentData: Partial<AgentConfig>): Promise<{ agent: AgentConfig }> => {
      return apiRequest('/api/generate-agent', {
        method: 'POST',
        body: JSON.stringify(agentData),
      });
    },
    onSuccess: async () => {
      // Force immediate refetch of agents list to include new agent and update count
      await queryClient.refetchQueries({ queryKey: dashboardKeys.agents() });
    },
  });
};

// Connect agent to Google Calendar
export const useConnectCalendar = () => {
  return useMutation({
    mutationFn: async (agentId: string): Promise<{ authUrl: string }> => {
      return apiRequest(`/api/agents/${agentId}/calendar/connect`, {
        method: 'POST',
      });
    },
  });
};

// Services Management Hooks
export interface ServicesManagementData {
  aiExtractedServices: Array<{
    name: string;
    documentDuration?: number | null;
  }> | string[];
  approvedServices: Array<{
    name: string;
    duration?: number;
    documentDuration?: number | null;
    isAIExtracted: boolean;
    stockStatus?: 'in_stock' | 'low_stock' | 'out_of_stock';
    approvedAt: string;
  }>;
  servicesReviewed: boolean;
  servicesLastReviewedAt?: string;
  businessName: string;
  businessType: string;
}

// Get services management data
export const useServicesManagement = (agentId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['services-management', agentId],
    queryFn: async (): Promise<ServicesManagementData> => {
      const response = await apiRequest(`/api/agents/${agentId}/services/management`);
      return response;
    },
    enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Approve services
export const useApproveServices = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ agentId, services }: { 
      agentId: string; 
      services: string[] | Array<{name: string; duration?: number; isAIExtracted?: boolean}> 
    }): Promise<any> => {
      return apiRequest(`/api/agents/${agentId}/services/approve`, {
        method: 'POST',
        body: JSON.stringify({ services }),
      });
    },
    onSuccess: (_, variables) => {
      // Invalidate services management query to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['services-management', variables.agentId] });
    },
  });
};

// Add a new service manually
export const useAddService = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ agentId, serviceName }: { agentId: string; serviceName: string }): Promise<any> => {
      return apiRequest(`/api/agents/${agentId}/services/add`, {
        method: 'POST',
        body: JSON.stringify({ serviceName }),
      });
    },
    onSuccess: (_, variables) => {
      // Invalidate services management query to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['services-management', variables.agentId] });
    },
  });
};

// Delete a service
export const useDeleteService = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ agentId, serviceIndex }: { agentId: string; serviceIndex: number }): Promise<any> => {
      return apiRequest(`/api/agents/${agentId}/services/${serviceIndex}`, {
        method: 'DELETE',
      });
    },
    // Optimistic update for instant UI feedback
    onMutate: async ({ agentId, serviceIndex }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['services-management', agentId] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(['services-management', agentId]);

      // Optimistically update the cache
      queryClient.setQueryData(['services-management', agentId], (old: ServicesManagementData | undefined) => {
        if (!old) return old;
        
        const newApprovedServices = [...old.approvedServices];
        newApprovedServices.splice(serviceIndex, 1);
        
        return {
          ...old,
          approvedServices: newApprovedServices
        };
      });

      // Return context for potential rollback
      return { previousData };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(['services-management', variables.agentId], context.previousData);
      }
    },
    onSettled: (_, __, variables) => {
      // Always refetch to ensure consistency (but the UI already updated)
      queryClient.invalidateQueries({ queryKey: ['services-management', variables.agentId] });
    },
  });
};

// Refresh services (force AI re-analysis)
export const useRefreshServices = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (agentId: string): Promise<any> => {
      return apiRequest(`/api/agents/${agentId}/services/refresh`, {
        method: 'POST',
      });
    },
    onSuccess: (_, agentId) => {
      // Invalidate services management query to show fresh data
      queryClient.invalidateQueries({ queryKey: ['services-management', agentId] });
    },
  });
};

// Fetch analytics data for a specific agent and timeframe
export const useAnalyticsData = (agentId: string | null, timeframe: string) => {
  return useQuery({
    queryKey: dashboardKeys.analytics(agentId || '', timeframe),
    queryFn: async (): Promise<{ analytics: AnalyticsData }> => {
      if (!agentId) {
        throw new Error('No agent selected');
      }
      return apiRequest(`/api/agents/${agentId}/analytics?timeframe=${timeframe}`);
    },
    enabled: !!agentId, // Only run query when agentId is available
    staleTime: 2 * 60 * 1000, // 2 minutes (analytics data changes more frequently)
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on 404 (agent not found)
      if (error?.message?.includes('404') || error?.status === 404) {
        return false;
      }
      // Retry network errors up to 2 times
      return failureCount < 2;
    },
  });
};

// Hook for updating service stock status
export const useUpdateServiceStockStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ agentId, serviceIndex, stockStatus }: { 
      agentId: string; 
      serviceIndex: number; 
      stockStatus: 'in_stock' | 'low_stock' | 'out_of_stock';
    }) => {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/agents/${agentId}/services/${serviceIndex}/stock-status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ stockStatus })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update stock status: ${response.statusText}`);
      }

      return response.json();
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch services data
      queryClient.invalidateQueries({ queryKey: ['services-management', variables.agentId] });
    },
    onError: (error) => {
      console.error('Stock status update error:', error);
    }
  });
};